import { isoDateTime, z } from "../zod.js";
import { Currency, Uuid } from "../identity/admin-schemas.js";

// Module 06 — Inventory: the P1 contract DTOs (spec 06 §5 request/response
// bodies) + the RFC 9457 problem-code catalog (spec 06 §3/§9). PHASE 1 =
// schemas only; the OpenAPI PATH registration (inventory/register.ts) and the
// generated api-client routes land in Phase 2 (deferred so no controller-less
// route breaks the contract/coverage gates — exactly how module 02 Phase 1 did
// it). These schemas are therefore NOT yet referenced by any registered path —
// they compile + export as the typed contract the services + controllers will
// bind to.
//
// Conventions carried from onboarding/schemas.ts: uuid ids via `Uuid`; money as
// integer minor units (`z.number().int()`, never a float); business
// dates as `format: date` strings; cost fields the server redacts are
// `.nullable()` with a sibling `redacted` marker (spec 06 §7 — "fields as null
// with a `"redacted": true` marker").
//
// EXACTNESS (D-041/D-042): every quantity/measure on this wire is a DECIMAL
// STRING mirroring numeric(18,6) — the exact text Postgres returns — never a
// JSON number. A float in the movement request is precisely what the
// exact-arithmetic doctrine exists to keep out of an append-only ledger; the
// kernel (`measure()` in @assessly/domain-kernel) parses these strings and
// refuses precision loss. This is a deliberate divergence from module 01's
// legacy `factor: z.number()` write path, which D-041 documents as a tracked
// defect — a NEW contract does not repeat it.

// --- shared scalars ---

/**
 * The MODULE NUMBER that owns a term this deployment cannot answer — `"10"`,
 * `"04"`, `"05"`, `"09"`.
 *
 * It exists because the honest-degradation fields (`owner_module` on an ATP
 * term, on a `degraded[]` entry) travel as an English SENTENCE —
 * `"procurement (Module 10)"` — and the web keys its capability copy by module
 * NUMBER. The screens therefore had to parse prose to decide what to render,
 * and any rewording or localisation silently dropped them back to the generic
 * "Waiting on Module …" line, i.e. precisely where honest-degradation copy
 * matters most (found by M06-S11, 2026-08-02). The sentence stays as HUMAN copy
 * for support and logs; this is the field a client keys off. Additive and
 * optional, so no existing consumer breaks.
 */
export const OwnerModuleCode = z
  .string()
  .regex(/^\d{2}$/)
  .openapi({ example: "10", description: "Two-digit module number that owns the missing term" });

/** A `numeric(18,6)` quantity/measure as its exact decimal text (D-041).
 * Zero IS admitted — response fields legitimately carry it (a drained unit's
 * `remaining_measure`, `received_qty_base` before receipt). Request fields
 * whose DB CHECK is `> 0` use {@link InventoryPositiveMeasure}. */
export const InventoryMeasure = z
  .string()
  .regex(/^\d{1,12}(\.\d{1,6})?$/)
  .openapi({ example: "1543.000000", description: "Exact decimal, ≤ 6 dp (numeric(18,6))" });
/** A strictly positive measure — movement/reservation quantities (DB CHECK
 * `> 0`). Without this, `"0"`/`"0.000000"` passed the contract and only died
 * at the unnamed DB CHECK as a 500 on the single ledger write path. */
export const InventoryPositiveMeasure = z
  .string()
  .regex(/^(?=.*[1-9])\d{1,12}(\.\d{1,6})?$/)
  .openapi({
    example: "1543.000000",
    description: "Exact decimal, ≤ 6 dp, strictly positive (numeric(18,6) CHECK > 0)",
  });
/** A signed measure (variances, deltas). */
export const InventorySignedMeasure = z
  .string()
  .regex(/^-?\d{1,12}(\.\d{1,6})?$/)
  .openapi({ example: "-50.000000" });
/** Integer minor units (money) — never a float (the money convention). */
const MinorMoney = z.number().int();
/** ISO-8601 business date (facility timezone). */
const BusinessDate = z.string().openapi({ format: "date", example: "2026-08-15" });
const Cursor = z.string().nullable();

// --- closed vocabularies (spec 06 §2) ---

export const LocationKind = z.enum([
  "warehouse",
  "zone",
  "bin",
  "receiving",
  "shipping",
  "in_transit",
  "quarantine",
  "scrap",
  "production",
  "customer",
  "supplier",
]);
export const LotOrigin = z.enum([
  "purchased",
  "produced",
  "reprocessed",
  "split",
  "opening_balance",
  "adjustment",
]);
export const LotQcStatus = z.enum(["pending", "released", "quarantined", "rejected"]);
export const StockUnitKind = z.enum([
  "bobbin",
  "drum",
  "barrel",
  "coil",
  "roll",
  "container",
  "pallet",
  "box",
  "piece",
]);
export const StockUnitStatus = z.enum([
  "empty",
  "filled",
  "in_use",
  "in_transit",
  "quarantined",
  "consumed",
  "shipped",
  "retired",
]);
export const MovementType = z.enum([
  "receipt",
  "production_output",
  "issue",
  "transfer_out",
  "transfer_in",
  "adjustment",
  "count_adjustment",
  "cut_out",
  "cut_in",
  "scrap",
  "return_to_supplier",
  "shipment",
  "repack",
  "reversal",
  // 04-B-4 (spec 06 §2.4, D-076): unconsumed work-order material carried back
  // to stock, production → bin|receiving. Its own physical event — never a
  // `reversal` (§3.9 mirrors are corrections of mis-keys; the common factory
  // return is partial and real). Posted only by the trusted WO return flow.
  "material_return",
]);
export const SourceDocType = z.enum([
  "purchase_order",
  "work_order",
  "sales_shipment",
  "transfer",
  "cycle_count",
  "cut",
  "waste_ticket",
  "manual_adjustment",
  "opening_balance",
]);
// Includes 'cut_tolerance' — the §3.2 step-4 residue write-off reason (§2.4
// list amended in the same PR, D-041 pt 5).
export const MovementReasonCode = z.enum([
  "damage",
  "count_variance",
  "data_correction",
  "loss",
  "found",
  "qc_fail",
  "shrinkage",
  "remeasure",
  "cut_tolerance",
]);
export const ReservationDemandType = z.enum([
  "sales_order_line",
  "work_order_component",
  "transfer_line",
  "quote_hold",
  "maintenance_order_part",
]);
/** The USER-mintable demand kinds (§2.6/§5 row 29, amended 2026-08-01 fix
 * round): `transfer_line` holds are SYSTEM-managed — auto-created by §3.3
 * dispatch, fulfilled by receive, released by cancel. A manually minted one
 * would be invisible to ATP's reserved term (D-048 pt 1) yet consume the
 * unit's one-active-hold slot, and a forged demand_id naming a real transfer
 * line would be silently resolved by that line's receive — so the create
 * request refuses the kind at the contract. */
export const UserReservationDemandType = z.enum([
  "sales_order_line",
  "work_order_component",
  "quote_hold",
  "maintenance_order_part",
]);
export const ReservationStatus = z.enum(["active", "fulfilled", "released", "expired"]);
export const TransferStatus = z.enum([
  "draft",
  "dispatched",
  "partially_received",
  "received",
  "cancelled",
]);
export const CycleCountStatus = z.enum([
  "draft",
  "in_progress",
  "review",
  "pending_approval",
  "posted",
  "cancelled",
]);
export const CountLineStatus = z.enum([
  "pending",
  "counted",
  "recount_requested",
  "accepted",
  "rejected",
]);
export const CountFrequency = z.enum(["daily", "weekly", "monthly", "quarterly"]);
export const ScanAction = z.enum([
  "lookup",
  "locate",
  "count",
  "receive",
  "issue",
  "cut",
  "transfer_dispatch",
  "transfer_receive",
  "remeasure",
]);
export const ScanResolvedKind = z.enum(["stock_unit", "location", "lot", "unknown"]);
export const GenealogyRelation = z.enum([
  "consumed_into",
  "cut_from",
  "reprocessed_into",
  "repacked_into",
]);
export const GenealogyNodeKind = z.enum(["lot", "stock_unit"]);
export const AdjustmentRequestStatus = z.enum([
  "pending_approval",
  "posted",
  "rejected",
  "cancelled",
]);

// --- entities (spec 06 §2) ---

export const InventoryLocation = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    parent_location_id: Uuid.nullable(),
    code: z.string(),
    name: z.string(),
    kind: LocationKind,
    path: z.string(),
    qr_payload: z.string().nullable(),
    is_countable: z.boolean(),
    allow_negative: z.boolean(),
    is_active: z.boolean(),
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("InventoryLocation");

export const InventoryLot = z
  .object({
    id: Uuid,
    product_id: Uuid,
    lot_code: z.string(),
    origin: LotOrigin,
    supplier_id: Uuid.nullable(),
    supplier_lot_code: z.string().nullable(),
    work_order_id: Uuid.nullable(),
    qc_status: LotQcStatus,
    manufactured_on: BusinessDate.nullable(),
    received_on: BusinessDate.nullable(),
    expires_on: BusinessDate.nullable(),
    attributes: z.record(z.string(), z.unknown()),
    note: z.string().nullable(),
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("InventoryLot");

export const StockUnit = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    serial_code: z.string(),
    kind: StockUnitKind,
    product_id: Uuid.nullable(),
    lot_id: Uuid.nullable(),
    measure_uom: z.enum(["m", "kg", "pcs"]).nullable(),
    capacity_measure: InventoryMeasure.nullable(),
    initial_measure: InventoryMeasure.nullable(),
    claimed_measure: InventoryMeasure.nullable(),
    remaining_measure: InventoryMeasure.nullable(),
    tare_weight_g: z.number().int().nullable(),
    gross_weight_g: z.number().int().nullable(),
    is_offcut: z.boolean(),
    parent_stock_unit_id: Uuid.nullable(),
    status: StockUnitStatus,
    current_location_id: Uuid.nullable(),
    /** Location breadcrumb path, e.g. `WH1/A/A-03` (§3.8). */
    location_path: z.string().nullable(),
    last_scanned_at: isoDateTime().nullable(),
    last_scanned_by: Uuid.nullable(),
    qr_payload: z.string().nullable(),
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("StockUnit");

/** Cost fields are role-redacted (§7): null + `redacted: true` for callers
 * without the inventory.cost.read field grant. */
export const StockMovement = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    movement_type: MovementType,
    product_id: Uuid.nullable(),
    lot_id: Uuid.nullable(),
    stock_unit_id: Uuid.nullable(),
    from_location_id: Uuid,
    to_location_id: Uuid,
    quantity: InventoryMeasure,
    uom: z.string(),
    qty_base: InventoryMeasure,
    unit_cost_minor: MinorMoney.nullable(),
    currency: Currency.nullable(),
    redacted: z.boolean().optional(),
    source_doc_type: SourceDocType.nullable(),
    source_doc_id: Uuid.nullable(),
    reversal_of_movement_id: Uuid.nullable(),
    reason_code: MovementReasonCode.nullable(),
    note: z.string().nullable(),
    moved_at: isoDateTime(),
    performed_by: Uuid,
    /** The work order the movement was recorded against (spec 06 §2.4, D-391 ruling 3). */
    ref_work_order_id: Uuid.nullable(),
    created_at: isoDateTime(),
  })
  .openapi("StockMovement");

export const StockLevel = z
  .object({
    facility_id: Uuid,
    product_id: Uuid,
    lot_id: Uuid.nullable(),
    location_id: Uuid,
    stock_unit_id: Uuid.nullable(),
    on_hand: InventorySignedMeasure,
    uom: z.string(),
    updated_at: isoDateTime(),
  })
  .openapi("StockLevel");

export const Reservation = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    product_id: Uuid,
    lot_id: Uuid.nullable(),
    stock_unit_id: Uuid.nullable(),
    quantity: InventoryMeasure,
    uom: z.string(),
    qty_base: InventoryMeasure,
    demand_type: ReservationDemandType,
    demand_id: Uuid,
    /** §2.6: the HUMAN reference this hold is placed against, in the words the
     * user typed ("BuildCo · 200 m 70 mm² · Thursday"). For `quote_hold` it is
     * the SOURCE of `demand_id` (uuid v5 over tenant + normalized ref), so the
     * same reference always resolves to the same demand; for the
     * module-minted kinds it is an optional label beside an id that already
     * names a real row. `null` = the hold names only its module-owned demand. */
    demand_ref: z.string().nullable(),
    status: ReservationStatus,
    expires_at: isoDateTime().nullable(),
    created_by: Uuid,
    created_at: isoDateTime(),
    released_at: isoDateTime().nullable(),
  })
  .openapi("InventoryReservation");

export const TransferLine = z
  .object({
    id: Uuid,
    line_no: z.number().int().min(1),
    product_id: Uuid.nullable(),
    lot_id: Uuid.nullable(),
    stock_unit_id: Uuid.nullable(),
    quantity: InventoryMeasure,
    uom: z.string(),
    qty_base: InventoryMeasure,
    received_qty_base: InventoryMeasure,
    receive_variance_reason: z.string().nullable(),
  })
  .openapi("TransferLine");

export const Transfer = z
  .object({
    id: Uuid,
    transfer_code: z.string(),
    from_facility_id: Uuid,
    to_facility_id: Uuid,
    from_location_id: Uuid.nullable(),
    to_location_id: Uuid.nullable(),
    status: TransferStatus,
    carrier: z.string().nullable(),
    vehicle_ref: z.string().nullable(),
    eta_on: BusinessDate.nullable(),
    note: z.string().nullable(),
    dispatched_at: isoDateTime().nullable(),
    /** §5 row 32 (amended 2026-08-01, fix round): the actor principals ride
     * the header so a client can reconstruct the timeline (created →
     * dispatched → received/cancelled, each with who + when). created_by is
     * nullable for pre-0009 rows only. */
    dispatched_by: Uuid.nullable(),
    received_at: isoDateTime().nullable(),
    received_by: Uuid.nullable(),
    cancelled_at: isoDateTime().nullable(),
    cancelled_by: Uuid.nullable(),
    cancel_reason: z.string().nullable(),
    created_by: Uuid.nullable(),
    /** The module 03 planned order this draft was converted from (spec 06 §2.7, D-389 ruling 5); null for a transfer a person created. */
    planned_order_id: Uuid.nullable(),
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("InventoryTransfer");

export const CycleCountSchedule = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    name: z.string(),
    scope_location_id: Uuid,
    product_family_id: Uuid.nullable(),
    frequency: CountFrequency,
    next_run_on: BusinessDate,
    blind: z.boolean(),
    is_active: z.boolean(),
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("CycleCountSchedule");

export const CycleCount = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    count_code: z.string(),
    schedule_id: Uuid.nullable(),
    scope_location_id: Uuid,
    blind: z.boolean(),
    status: CycleCountStatus,
    snapshot_at: isoDateTime().nullable(),
    started_at: isoDateTime().nullable(),
    submitted_at: isoDateTime().nullable(),
    approved_at: isoDateTime().nullable(),
    approval_request_id: Uuid.nullable(),
    posted_at: isoDateTime().nullable(),
    cancelled_at: isoDateTime().nullable(),
    cancel_reason: z.string().nullable(),
    variance_value_minor: MinorMoney.nullable(),
    currency: Currency.nullable(),
    redacted: z.boolean().optional(),
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("CycleCount");

/** `expected_qty_base` is OPTIONAL, not nullable: the server OMITS it entirely
 * while the count is blind ∧ in_progress and the caller lacks
 * inventory.count.review (§7 — blind means blind). */
export const CycleCountLine = z
  .object({
    id: Uuid,
    location_id: Uuid,
    product_id: Uuid,
    lot_id: Uuid.nullable(),
    stock_unit_id: Uuid.nullable(),
    expected_qty_base: InventorySignedMeasure.optional(),
    counted_qty_base: InventoryMeasure.nullable(),
    counted_measure: InventoryMeasure.nullable(),
    uom: z.string(),
    variance_qty_base: InventorySignedMeasure.nullable(),
    variance_value_minor: MinorMoney.nullable(),
    redacted: z.boolean().optional(),
    line_status: CountLineStatus,
    recount_of_line_id: Uuid.nullable(),
    counted_by: Uuid.nullable(),
    counted_at: isoDateTime().nullable(),
    note: z.string().nullable(),
  })
  .openapi("CycleCountLine");

/** `approval_request_id` is the Module 12 chain instance — NULL while the
 * local approval tier decides (APPROVAL_CHAIN_PORT unbound, D-049: a chain id
 * is never fabricated). `decided_by` is the local-tier resolver. */
export const AdjustmentRequest = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    movement_payload: z.record(z.string(), z.unknown()),
    est_value_minor: MinorMoney.nullable(),
    currency: Currency.nullable(),
    redacted: z.boolean().optional(),
    status: AdjustmentRequestStatus,
    approval_request_id: Uuid.nullable(),
    posted_movement_id: Uuid.nullable(),
    reject_reason: z.string().nullable(),
    requested_by: Uuid,
    requested_at: isoDateTime(),
    resolved_at: isoDateTime().nullable(),
    decided_by: Uuid.nullable(),
    /** The work order the request was recorded against (spec 06 §2.12, D-391 ruling 3). */
    ref_work_order_id: Uuid.nullable(),
  })
  .openapi("AdjustmentRequest");

export const ScanEvent = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    scanned_payload: z.string(),
    resolved_kind: ScanResolvedKind,
    resolved_id: Uuid.nullable(),
    context_location_id: Uuid.nullable(),
    action: ScanAction,
    device_id: z.string().nullable(),
    app_area: z.enum(["floor", "office"]),
    payload: z.record(z.string(), z.unknown()),
    performed_by: Uuid,
    occurred_at: isoDateTime(),
  })
  .openapi("InventoryScanEvent");

// --- locations (§5 rows 1–5) ---

export const LocationListResponse = z
  .object({ items: z.array(InventoryLocation), next_cursor: Cursor })
  .openapi("LocationListResponse");

/** Location codes compose the `/`-joined materialized path (§2.1), so the
 * charset is restricted to path-safe characters: no `/` (the separator), no
 * whitespace, no SQL-LIKE metacharacters. Amended 2026-08-01 (review): an
 * unconstrained code could embed `/` and corrupt path semantics outright. */
const LocationCode = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,39}$/)
  .openapi({
    example: "A-03",
    description:
      "1–40 chars; letters, digits, '.', '_', '-' (must start alphanumeric) — path-safe (§2.1)",
  });

export const LocationCreateRequest = z
  .object({
    facility_id: Uuid,
    code: LocationCode,
    name: z.string().min(1).max(120),
    kind: LocationKind,
    parent_location_id: Uuid.nullable().optional(),
  })
  .openapi("LocationCreateRequest");

export const LocationPatchRequest = z
  .object({
    name: z.string().min(1).max(120).optional(),
    parent_location_id: Uuid.nullable().optional(),
    is_countable: z.boolean().optional(),
    is_active: z.boolean().optional(),
    allow_negative: z.boolean().optional(),
  })
  .openapi("LocationPatchRequest");

// §5 row 5 — batch label PDF. The renderer is Module 02's QR print-kit surface
// (spec 02 §2.8): this endpoint creates a `location_labels` batch and answers
// with a presigned link, so the two doors onto label printing share one audit
// trail and one `channels.qr_kit_printed` fact.
export const LocationQrLabelsRequest = z
  .object({
    location_ids: z.array(Uuid).min(1).max(1000),
    facility_id: Uuid.nullable().optional(),
  })
  .openapi("LocationQrLabelsRequest");

export const LocationQrLabelsResponse = z
  .object({
    batch_id: Uuid,
    item_count: z.number().int().min(0),
    /** The file registry's presigned GET, 5 min (D-322). Null when object storage is momentarily
     * unreachable — the batch is still ready and GET /v1/qr-kits/{id} re-mints
     * the link. */
    pdf_url: z.string().nullable(),
  })
  .openapi("LocationQrLabelsResponse");

export const LocationContentsResponse = z
  .object({
    levels: z.array(
      z.object({
        product_id: Uuid,
        lot_id: Uuid.nullable(),
        stock_unit_id: Uuid.nullable(),
        on_hand: InventorySignedMeasure,
        uom: z.string(),
      }),
    ),
  })
  .openapi("LocationContentsResponse");

export const QrLabelsRequest = z
  .object({ location_ids: z.array(Uuid).min(1).max(500) })
  .openapi("QrLabelsRequest");

export const QrLabelsResponse = z.object({ pdf_url: z.url() }).openapi("QrLabelsResponse");

// --- lots (§5 rows 6–11) ---

export const LotListResponse = z
  .object({ items: z.array(InventoryLot), next_cursor: Cursor })
  .openapi("LotListResponse");

export const LotCreateRequest = z
  .object({
    product_id: Uuid,
    lot_code: z.string().min(1).max(80).optional(),
    origin: LotOrigin,
    supplier_id: Uuid.optional(),
    supplier_lot_code: z.string().max(120).optional(),
    work_order_id: Uuid.optional(),
    manufactured_on: BusinessDate.optional(),
    received_on: BusinessDate.optional(),
    expires_on: BusinessDate.optional(),
    attributes: z.record(z.string(), z.unknown()).optional(),
    note: z.string().max(2000).optional(),
  })
  .openapi("LotCreateRequest");

export const LotDetailResponse = z
  .object({
    lot: InventoryLot,
    stock_summary: z.array(
      z.object({
        location_id: Uuid,
        location_path: z.string(),
        on_hand: InventorySignedMeasure,
        uom: z.string(),
      }),
    ),
    units: z.array(StockUnit),
    /** Total units in the lot visible to the caller — units[] carries at most
     * 200 (newest first); when truncated, page GET /stock-units?lot_id=. */
    units_total: z.number().int().nonnegative(),
    units_truncated: z.boolean(),
  })
  .openapi("LotDetailResponse");

export const LotQuarantineRequest = z
  .object({ reason: z.string().min(1).max(2000) })
  .openapi("LotQuarantineRequest");

export const LotReleaseRequest = z
  .object({ note: z.string().max(2000).optional() })
  .openapi("LotReleaseRequest");

export const LotQcStatusResponse = z
  .object({ qc_status: LotQcStatus })
  .openapi("LotQcStatusResponse");

// --- genealogy trace (§5 row 11; §3.7) ---

export const TraceNode = z
  .object({
    kind: GenealogyNodeKind,
    id: Uuid,
    label: z.string(),
    depth: z.number().int().min(0),
    /** Lot nodes only (§3.7 upstream claim path, M06-S09): the purchased lot's
     * supplier reference travels with the node so a claim can be filed without
     * a second lookup. */
    supplier_id: Uuid.nullable().optional(),
    supplier_lot_code: z.string().nullable().optional(),
    qc_status: LotQcStatus.nullable().optional(),
  })
  .openapi("TraceNode");

export const TraceEdge = z
  .object({
    parent_kind: GenealogyNodeKind,
    parent_id: Uuid,
    child_kind: GenealogyNodeKind,
    child_id: Uuid,
    relation: GenealogyRelation,
    quantity: InventoryMeasure,
    uom: z.string(),
    work_order_id: Uuid.nullable(),
  })
  .openapi("TraceEdge");

/** Downstream summary or upstream summary — the §5 row-11 union. Customer
 * names are assignment-filtered for sales (§7). */
export const TraceSummary = z
  .object({
    finished_lots: z.number().int().min(0).optional(),
    stock_units: z.number().int().min(0).optional(),
    shipments: z.number().int().min(0).optional(),
    customers: z.number().int().min(0).optional(),
    /** Downstream only, present when `customer_resolution` is `partial`: how many
     * of `shipments` name no customer this caller can be told — the number that
     * keeps `customers` from reading as the whole answer on a recall. */
    unresolved_shipments: z.number().int().min(0).optional(),
    raw_lots: z.number().int().min(0).optional(),
    suppliers: z.number().int().min(0).optional(),
  })
  .openapi("TraceSummary");

/** A terminal shipment joined onto the downstream trace (§3.7): the module's
 * own ledger rows, present whether or not Module 09 resolves customers. */
export const TraceShipment = z
  .object({
    movement_id: Uuid,
    stock_unit_id: Uuid.nullable(),
    lot_id: Uuid.nullable(),
    source_doc_id: Uuid.nullable(),
    qty_base: InventoryMeasure,
    uom: z.string(),
    moved_at: isoDateTime(),
  })
  .openapi("TraceShipment");

export const TraceCustomer = z
  .object({ customer_id: Uuid, customer_name: z.string() })
  .openapi("TraceCustomer");

export const TraceResponse = z
  .object({
    direction: z.enum(["up", "down"]),
    nodes: z.array(TraceNode),
    edges: z.array(TraceEdge),
    summary: TraceSummary,
    truncated: z.boolean(),
    /** Boundary nodes when depth/node caps hit, or cycle members on corrupt
     * data (§9.13) — corrupt data must never read as clean. */
    boundary: z.array(TraceNode).optional(),
    /** Downstream only: the shipment movements touching traced nodes. */
    shipments: z.array(TraceShipment).optional(),
    /** Downstream only, and it is a claim about COMPLETENESS, never only about
     * wiring. `resolved` = EVERY shipment on the trace named a customer, so
     * `customers` is the whole answer. `partial` (D-286 ruling 24) = the lookup
     * is bound but at least one shipment names no customer this caller can be
     * told — a ledger shipment with no module-09 shipment document behind it
     * (seeded or pre-module-09 stock), or one outside the caller's facility
     * fence; `customers` holds what DID resolve and
     * `summary.unresolved_shipments` says how many did not. `unavailable` = no
     * lookup is bound at all. A recall reviewer must never read an empty or
     * short `customers` list as "nobody else was affected". */
    customer_resolution: z.enum(["resolved", "partial", "unavailable"]).optional(),
    customers: z.array(TraceCustomer).optional(),
  })
  .openapi("TraceResponse");

// --- stock units (§5 rows 12–19) ---

export const StockUnitListResponse = z
  .object({ items: z.array(StockUnit), next_cursor: Cursor })
  .openapi("StockUnitListResponse");

export const StockUnitRegisterRequest = z
  .object({
    serial_code: z.string().min(1).max(80),
    kind: StockUnitKind,
    product_id: Uuid.optional(),
    lot_id: Uuid.optional(),
    capacity_measure: InventoryMeasure.optional(),
    initial_measure: InventoryMeasure.optional(),
    claimed_measure: InventoryMeasure.optional(),
    measure_uom: z.enum(["m", "kg", "pcs"]).optional(),
    tare_weight_g: z.number().int().min(0).optional(),
    location_id: Uuid,
  })
  .openapi("StockUnitRegisterRequest");

export const StockUnitCardResponse = z
  .object({
    unit: StockUnit,
    active_reservation: Reservation.nullable(),
    last_scans: z.array(ScanEvent),
  })
  .openapi("StockUnitCardResponse");

/** Movements + scan events merged newest-first (§5 row 15); cost fields
 * role-redacted per §7. */
export const StockUnitHistoryResponse = z
  .object({
    items: z.array(
      z.union([
        z.object({ kind: z.literal("movement"), movement: StockMovement }),
        z.object({ kind: z.literal("scan"), scan: ScanEvent }),
      ]),
    ),
    next_cursor: Cursor,
  })
  .openapi("StockUnitHistoryResponse");

export const CutPieceRequest = z
  .object({
    length: InventoryMeasure,
    kind: z.enum(["child", "waste"]).default("child"),
    /** Pre-printed label serial; auto `{parent}-{n}` when omitted (§3.2). */
    serial_code: z.string().min(1).max(80).optional(),
  })
  .openapi("CutPieceRequest");

export const CutRequest = z
  .object({
    pieces: z.array(CutPieceRequest).min(1).max(50),
    /** Material lost per physical SEPARATION (blade width) — charged once per
     * cut by the kernel planner (§3.2: n pieces consuming the whole source
     * need n−1 cuts). Omitted ⇒ 0. */
    kerf: InventoryPositiveMeasure.optional(),
    /** `qc_fail` sets children quarantined (§3.2 QC variant). */
    reason: z.string().max(200).optional(),
    work_order_id: Uuid.optional(),
  })
  .openapi("CutRequest");

export const CutResponse = z
  .object({
    children: z.array(
      z.object({
        id: Uuid,
        serial_code: z.string(),
        remaining_measure: InventoryMeasure,
        is_offcut: z.boolean(),
        quarantined: z.boolean(),
      }),
    ),
    waste_pieces: z.array(z.object({ length: InventoryMeasure, reason: z.string().optional() })),
    parent_remaining_measure: InventoryMeasure,
    parent_status: StockUnitStatus,
    /** §3.2 step 5: set when a source-unit reservation could not be re-pointed
     * to a single covering child. */
    reservation_not_covered: z.boolean().optional(),
  })
  .openapi("CutResponse");

export const RemeasureRequest = z
  .object({ entered_measure: InventoryMeasure })
  .openapi("RemeasureRequest");

export const RemeasureResponse = z
  .object({
    adjusted: z.boolean(),
    delta: InventorySignedMeasure,
    remaining_measure: InventoryMeasure,
    adjustment_movement_id: Uuid.nullable(),
  })
  .openapi("RemeasureResponse");

/** §2.4 quarantine clarification: unit-level quarantine that physically
 * relocates posts a `repack` to the facility quarantine location
 * (`relocate: true`); quarantining in place is STATUS-ONLY (default). */
export const UnitQuarantineRequest = z
  .object({
    reason: z.string().min(1).max(2000),
    relocate: z.boolean().optional(),
  })
  .openapi("UnitQuarantineRequest");

/** §2.4: QC release that returns relocated stock to storage posts a `repack`
 * quarantine → `to_location_id`; release-in-place (omitted) posts nothing. */
export const UnitReleaseRequest = z
  .object({
    note: z.string().max(2000).optional(),
    to_location_id: Uuid.optional(),
  })
  .openapi("UnitReleaseRequest");

/** Shared response of the unit quarantine / release / retire status flows:
 * the resulting status + the repack/scrap movement when one was posted
 * (null for the status-only paths). */
export const UnitStatusResponse = z
  .object({
    status: StockUnitStatus,
    movement_id: Uuid.nullable(),
  })
  .openapi("UnitStatusResponse");

export const UnitRetireRequest = z
  .object({ reason_code: MovementReasonCode })
  .openapi("UnitRetireRequest");

// --- scan (§5 row 20; §3.5) ---

export const ScanRequest = z
  .object({
    scanned_payload: z.string().min(1),
    action: ScanAction,
    context_location_id: Uuid.optional(),
    entered_measure: InventoryMeasure.optional(),
    device_id: z.string().max(120).optional(),
    app_area: z.enum(["floor", "office"]).default("floor"),
  })
  .openapi("ScanRequest");

export const ScanResponse = z
  .object({
    scan_event_id: Uuid,
    resolved_kind: ScanResolvedKind,
    /** Entity snapshot; null for `unknown` (tenant isolation, §9.9 — a foreign
     * tenant's QR reveals nothing). */
    stock_unit: StockUnit.nullable().optional(),
    location: InventoryLocation.nullable().optional(),
    lot: InventoryLot.nullable().optional(),
    location_updated: z.boolean(),
    remeasure: RemeasureResponse.nullable().optional(),
  })
  .openapi("ScanResponse");

// --- lookup (§5 row 21; §3.8) ---

export const LookupUnitMatch = z
  .object({
    stock_unit_id: Uuid,
    serial_code: z.string(),
    product_id: Uuid,
    remaining_measure: InventoryMeasure,
    capacity_measure: InventoryMeasure.nullable(),
    uom: z.string(),
    lot_id: Uuid.nullable(),
    lot_qc_status: LotQcStatus.nullable(),
    facility_id: Uuid,
    location_path: z.string().nullable(),
    last_scanned_at: isoDateTime().nullable(),
    reserved: z.boolean(),
    /** §3.8 rank 1 / M06-S12: a held drum is only actionable when the badge
     * NAMES the demand. `demand_ref` is the human reference where the hold
     * carries one (§2.6) — "Reserved · BuildCo · 200 m Thursday" is what a
     * storekeeper can act on; the id's first eight hex characters are the
     * fallback for the module-minted kinds, which have no words. */
    reservation_demand_ref: z
      .object({
        demand_type: ReservationDemandType,
        demand_id: Uuid,
        demand_ref: z.string().nullable(),
      })
      .nullable(),
  })
  .openapi("LookupUnitMatch");

export const LookupOffcutCombo = z
  .object({
    pieces: z.array(
      z.object({
        stock_unit_id: Uuid,
        serial_code: z.string(),
        remaining_measure: InventoryMeasure,
        location_path: z.string().nullable(),
      }),
    ),
    combined_measure: InventoryMeasure,
    surplus: InventoryMeasure,
    /** Module 05 owns off-cut pricing (D-047 pt 5): while its port is unbound
     * the hint is null with `discount_source: 'unavailable'` — combos
     * themselves are computed locally by the shared kernel matcher and always
     * present. */
    discount_hint: z.string().nullable(),
    discount_source: z.enum(["module_05", "unavailable"]),
  })
  .openapi("LookupOffcutCombo");

/** §3.8: a candidate off-cut piece the matcher refused, with the first failing
 * reason — "3 nearby pieces are quarantined" instead of a silently empty
 * group. `stock_unit_id` is null when the reason states a §7-hidden
 * quarantine/hold and the caller lacks `inventory.quarantine` /
 * `inventory.count.review` (the count is public, the identity is not —
 * ruled 2026-08-01). */
export const LookupOffcutRejection = z
  .object({
    stock_unit_id: Uuid.nullable(),
    reason: z.enum([
      "status_unavailable",
      "lot_not_released",
      "reserved",
      "other_facility",
      "non_positive_remaining",
    ]),
  })
  .openapi("LookupOffcutRejection");

export const LookupBulkLot = z
  .object({
    lot_id: Uuid,
    lot_code: z.string(),
    product_id: Uuid,
    location_id: Uuid,
    location_path: z.string().nullable(),
    on_hand: InventorySignedMeasure,
    uom: z.string(),
  })
  .openapi("LookupBulkLot");

/** Degradation is visible, never silent: combos are computed by THIS module
 * (the shared kernel matcher, D-047 pt 5) and always present; what an unbound
 * module 05 degrades is only the PRICING — `discount_hint` null with
 * `discount_source: 'unavailable'` and a `degraded[]` entry naming the owner.
 * `offcut_eligible_count` + `offcut_rejected` appear whenever the combos
 * group was computed (a required measure was given). */
/**
 * Every bound the lookup hit, stated on the wire (spec 06 §3.8 — "no cap is
 * silent", amended 2026-08-03). Always present, all four flags always emitted:
 * `false` everywhere is the caller's licence to read an empty group as "there
 * is none", which is precisely the conclusion a hidden cap corrupts.
 *
 * `product_resolution` is the serious one, because it is the only flag that
 * shortens every group at once instead of one of them. It carries ONE meaning
 * whichever way the answer was reached (D-298 ruling 10): it is `true` if and
 * only if some product that BOTH matched the query's tokens AND could have
 * contributed a row to any of the three groups went unsearched. So `true`
 * still means what it has always meant — stock of matching products is missing
 * from every group below — and it has never meant "the catalogue holds more
 * names than were looked at".
 *
 * That definition is what the two branches behind the flag share. Ordinarily
 * the tokens are resolved across the whole catalogue and the flag reports that
 * the match set was larger than the bound. When that happens, module 06 asks
 * its own ledger which products carry stock in the caller's role scope and
 * re-resolves the same tokens inside that id set: a product holding nothing
 * the caller could be shown is then not searched, and is not counted as
 * unsearched either, because it could have contributed no row to any group.
 *
 * It fails closed at both edges (D-298 ruling 9). Where the narrowing cannot
 * be completed — its own probe overflows, or the narrowed re-resolution
 * truncates in turn — the flag stays `true` rather than being talked down, so
 * a `true` can over-report.
 *
 * ONE case under-reports, and D-298 ruling 6 names it here rather than leaving
 * it to be discovered: on an escalated query a unit in ANY status group 1 returns — which includes a `quarantined` drum for the two roles that can see quarantined stock — sitting
 * at `remaining_measure = 0`, whose product carries no positive stock level
 * anywhere in scope, leaves the search without setting the flag. It is a drum
 * with nothing on it, and today it can appear at the bottom of a measure-less
 * group 1. Every other product that could contribute a row is still searched,
 * which is what keeps `false` everywhere the licence described above.
 */
export const LookupTruncation = z
  .object({
    product_resolution: z.boolean(),
    unit_matches: z.boolean(),
    offcut_candidates: z.boolean(),
    bulk_lots: z.boolean(),
  })
  .openapi("LookupTruncation");

export const LookupResponse = z
  .object({
    unit_matches: z.array(LookupUnitMatch),
    offcut_combos: z.array(LookupOffcutCombo),
    bulk_lots: z.array(LookupBulkLot),
    truncated: LookupTruncation,
    offcut_eligible_count: z.number().int().nonnegative().optional(),
    offcut_rejected: z.array(LookupOffcutRejection).optional(),
    degraded: z
      .array(
        z.object({
          term: z.string(),
          owner_module: z.string(),
          owner_module_code: OwnerModuleCode.optional(),
        }),
      )
      .optional(),
  })
  .openapi("LookupResponse");

// --- levels + ATP (§5 rows 22–23; §3.6) ---

export const LevelsResponse = z
  .object({ items: z.array(StockLevel), next_cursor: Cursor })
  .openapi("LevelsResponse");

/** One §3.6 ATP term. An unbound providing module reports `qty_base: null` +
 * `unavailable: true` + `owner_module` — an honest null, NEVER a fake 0 that
 * a planner would read as "no POs inbound" (spec 06 §3.6 amendment, 2c). */
export const AtpTerm = z
  .object({
    qty_base: InventorySignedMeasure.nullable(),
    unavailable: z.boolean().optional(),
    /** HUMAN copy, English, for support and logs — never key UI off this. */
    owner_module: z.string().optional(),
    /** The machine-readable half (D-056 pt 2 follow-up). */
    owner_module_code: OwnerModuleCode.optional(),
  })
  .openapi("AtpTerm");

export const AtpResponse = z
  .object({
    product_id: Uuid,
    facility_id: Uuid,
    date: BusinessDate,
    /** Total over the AVAILABLE terms only (§3.6 amendment, 2c). */
    atp_qty: InventorySignedMeasure,
    /** True whenever any term is unavailable — the total is a partial sum. */
    available_terms_only: z.boolean(),
    uom: z.string(),
    terms: z.object({
      on_hand: AtpTerm,
      reserved: AtpTerm,
      in_transit_inbound: AtpTerm,
      po_inbound: AtpTerm,
      wo_planned_output: AtpTerm,
      wo_demand: AtpTerm,
    }),
  })
  .openapi("AtpResponse");

// --- movements (§5 rows 24–26; §3.9) ---

export const MovementListResponse = z
  .object({ items: z.array(StockMovement), next_cursor: Cursor })
  .openapi("MovementListResponse");

/** §5 row 25: manual movement types only. `moved_at` is the operator-entered
 * business timestamp for late floor entries (defaults to now, §2.4).
 * `stock_unit_id` is accepted ONLY with `repack` (pure relocation, §2.4
 * amendment 2026-08-01): a manual scrap/issue/adjustment naming a unit would
 * change its on-hand while `remaining_measure` stays frozen — the dedicated
 * cut/remeasure/retire flows own quantity-changing unit operations. */
export const MovementCreateRequest = z
  .object({
    movement_type: z.enum(["adjustment", "repack", "scrap", "issue"]),
    product_id: Uuid.optional(),
    lot_id: Uuid.optional(),
    stock_unit_id: Uuid.optional(),
    from_location_id: Uuid,
    to_location_id: Uuid,
    quantity: InventoryPositiveMeasure,
    uom: z.string(),
    reason_code: MovementReasonCode.optional(),
    note: z.string().min(10).max(2000).optional(),
    moved_at: isoDateTime().optional(),
    /** The work order this movement is recorded against (spec 06 §3.9, D-391 ruling 3); 404 WORK_ORDER_NOT_FOUND when no such order exists. */
    work_order_id: Uuid.optional(),
  })
  .superRefine((data, ctx) => {
    if (data.stock_unit_id !== undefined && data.movement_type !== "repack") {
      ctx.addIssue({
        code: "custom",
        path: ["stock_unit_id"],
        message:
          "stock_unit_id is only accepted on repack — quantity-changing unit operations use the dedicated cut/remeasure/retire flows (spec 06 §2.4)",
      });
    }
  })
  .openapi("MovementCreateRequest");

/** `idempotent_replay: true` when the movement's domain idempotency key had
 * already been posted — the ORIGINAL movement id is returned, never a
 * duplicate row (§9.2). */
export const MovementCreateResponse = z
  .object({ movement_id: Uuid, idempotent_replay: z.boolean().optional() })
  .openapi("MovementCreateResponse");

/** The 202 body when the §3.9 value gate routes to approval.
 * `approval_request_id` is null while the local tier decides (D-049);
 * `idempotent_replay` marks a §9.2 replay of the same held request. */
export const AdjustmentRequestAcceptedResponse = z
  .object({
    adjustment_request_id: Uuid,
    approval_request_id: Uuid.nullable(),
    idempotent_replay: z.boolean().optional(),
  })
  .openapi("AdjustmentRequestAcceptedResponse");

export const MovementReverseRequest = z
  .object({ note: z.string().max(2000).optional() })
  .openapi("MovementReverseRequest");

export const MovementReverseResponse = z
  .object({ reversal_movement_id: Uuid, idempotent_replay: z.boolean().optional() })
  .openapi("MovementReverseResponse");

// --- receipts (§5 row 27; §3.1, §3.10) ---

/**
 * Money never travels without its currency (the money convention; the iff form the DB
 * uses on inventory_settings): a captured cost and its currency arrive
 * together or not at all. Enforced here so a mismatched pair is a 422 on the
 * `currency` path, not a Phase-2 500 at the stock_movements_cost_currency
 * DB CHECK.
 */
const costCurrencyTogether = (
  data: { unit_cost_minor?: number | undefined; currency?: string | undefined },
  ctx: z.RefinementCtx,
): void => {
  if ((data.unit_cost_minor === undefined) !== (data.currency === undefined)) {
    ctx.addIssue({
      code: "custom",
      path: ["currency"],
      message: "unit_cost_minor and currency must be provided together",
    });
  }
};

/** `initial_measure` may be omitted ONLY when `claimed_measure` is present
 * (§9.4: the operator skipped measuring — the service sets initial :=
 * claimed and flags `unverified_measure` on the created event). */
export const ReceiptUnitRequest = z
  .object({
    serial_code: z.string().min(1).max(80),
    kind: StockUnitKind,
    initial_measure: InventoryPositiveMeasure.optional(),
    claimed_measure: InventoryPositiveMeasure.optional(),
    /** Defaults to the product's base UoM when that is a §2.3 measure UoM. */
    measure_uom: z.enum(["m", "kg", "pcs"]).optional(),
    capacity_measure: InventoryPositiveMeasure.optional(),
    unit_cost_minor: MinorMoney.min(0).optional(),
    currency: Currency.optional(),
  })
  .superRefine(costCurrencyTogether)
  .superRefine((data, ctx) => {
    if (data.initial_measure === undefined && data.claimed_measure === undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["initial_measure"],
        message:
          "A received unit carries a measured value: initial_measure, or claimed_measure when measuring was skipped (spec 06 §3.1/§9.4)",
      });
    }
  })
  .openapi("ReceiptUnitRequest");

/** §3.7 regrind / production-consumption parentage: existing lots/units the
 * received lot descends from. Relation is derived from the lot origin
 * (`reprocessed` → `reprocessed_into`, else `consumed_into`). */
export const ReceiptParentRef = z
  .object({
    kind: GenealogyNodeKind,
    id: Uuid,
    /** Consumed quantity for the genealogy edge; defaults to the receipt's
     * total base quantity. */
    quantity: InventoryPositiveMeasure.optional(),
  })
  .openapi("ReceiptParentRef");

export const ReceiptCreateRequest = z
  .object({
    source_doc_type: SourceDocType,
    source_doc_id: Uuid.optional(),
    product_id: Uuid,
    lot_id: Uuid.optional(),
    lot: LotCreateRequest.omit({ product_id: true }).optional(),
    units: z.array(ReceiptUnitRequest).min(1).max(500).optional(),
    /** Bulk / opening balance: plain quantity instead of units[] (§3.10). */
    quantity: InventoryPositiveMeasure.optional(),
    uom: z.string().optional(),
    /** Lot-level cost for bulk / opening balance (§3.10.3); also the fallback
     * for units[] entries without their own cost. */
    unit_cost_minor: MinorMoney.min(0).optional(),
    currency: Currency.optional(),
    to_location_id: Uuid,
    /** §3.7: genealogy parents (regrind, production consumption). */
    parent_refs: z.array(ReceiptParentRef).max(100).optional(),
  })
  .superRefine(costCurrencyTogether)
  .superRefine((data, ctx) => {
    if (data.lot_id === undefined && data.lot === undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["lot"],
        message: "A receipt names an existing lot_id or the lot to create (spec 06 §3.1)",
      });
    }
    if (data.lot_id !== undefined && data.lot !== undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["lot"],
        message: "lot_id and lot are mutually exclusive",
      });
    }
    const hasUnits = data.units !== undefined;
    const hasBulk = data.quantity !== undefined;
    if (hasUnits === hasBulk) {
      ctx.addIssue({
        code: "custom",
        path: ["units"],
        message:
          "A receipt carries units[] (serialized) OR quantity+uom (bulk), exactly one (spec 06 §3.1/§3.10)",
      });
    }
    if (hasBulk && data.uom === undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["uom"],
        message: "A bulk receipt quantity carries its uom",
      });
    }
  })
  .openapi("ReceiptCreateRequest");

export const ReceiptCreateResponse = z
  .object({ lot_id: Uuid, unit_ids: z.array(Uuid), movement_ids: z.array(Uuid) })
  .openapi("ReceiptCreateResponse");

// --- reservations (§5 rows 28–30) ---

export const ReservationListResponse = z
  .object({ items: z.array(Reservation), next_cursor: Cursor })
  .openapi("ReservationListResponse");

/**
 * §5 row 29. `demand_id` and `demand_ref` are the TWO ways to name what a hold
 * is for, and which one is required depends on the kind (amended 2026-08-20,
 * D-151):
 *
 *   * `sales_order_line`, `work_order_component`, `maintenance_order_part` —
 *     `demand_id` REQUIRED. It names a row in the module that owns the demand,
 *     and that module mints both the row and the hold (module 04 mints its
 *     component holds at release). Deriving an id from words here would forge a
 *     pointer at a row that will never exist; the service refuses it 422
 *     `RESERVATION_INVALID` naming the module. `demand_ref` may ride along as
 *     a label.
 *   * `quote_hold` — EITHER. A hold placed while somebody quotes points at
 *     nothing until module 09 lands, so `demand_ref` (the words the user
 *     typed) is accepted alone and the service DERIVES `demand_id` from it —
 *     uuid v5 over (tenant, normalized reference), so the same reference always
 *     resolves to the same demand and every grouping surface keeps working.
 *     One of the two must be present.
 *
 * Both fields are optional HERE and the pairing is enforced in the service,
 * where the rule can name the module a caller is missing instead of failing as
 * a shape error.
 */
export const ReservationCreateRequest = z
  .object({
    product_id: Uuid,
    quantity: InventoryPositiveMeasure,
    uom: z.string(),
    lot_id: Uuid.optional(),
    stock_unit_id: Uuid.optional(),
    demand_type: UserReservationDemandType,
    demand_id: Uuid.optional(),
    demand_ref: z.string().trim().min(3).max(200).optional(),
    expires_at: isoDateTime().optional(),
    facility_id: Uuid,
    allow_backorder: z.boolean().default(false),
  })
  .openapi("ReservationCreateRequest");

export const ReservationCreateResponse = z
  .object({ reservation: Reservation })
  .openapi("ReservationCreateResponse");

export const ReservationReleaseResponse = z
  .object({ reservation: Reservation })
  .openapi("ReservationReleaseResponse");

// --- transfers (§5 rows 31–36; §3.3) ---

export const TransferLineRequest = z
  .object({
    product_id: Uuid.optional(),
    lot_id: Uuid.optional(),
    stock_unit_id: Uuid.optional(),
    quantity: InventoryPositiveMeasure,
    uom: z.string(),
  })
  .openapi("TransferLineRequest");

export const TransferCreateRequest = z
  .object({
    from_facility_id: Uuid,
    to_facility_id: Uuid,
    from_location_id: Uuid.optional(),
    to_location_id: Uuid.optional(),
    carrier: z.string().max(200).optional(),
    vehicle_ref: z.string().max(200).optional(),
    eta_on: BusinessDate.optional(),
    note: z.string().max(2000).optional(),
    lines: z.array(TransferLineRequest).min(1).max(500),
  })
  .openapi("TransferCreateRequest");

export const TransferPatchRequest = z
  .object({
    carrier: z.string().max(200).optional(),
    vehicle_ref: z.string().max(200).optional(),
    eta_on: BusinessDate.nullable().optional(),
    note: z.string().max(2000).nullable().optional(),
    /** Draft-only line mutations (§3.3): full replacement of the line set. */
    lines: z.array(TransferLineRequest).min(1).max(500).optional(),
  })
  .openapi("TransferPatchRequest");

export const TransferListResponse = z
  .object({ items: z.array(Transfer), next_cursor: Cursor })
  .openapi("TransferListResponse");

export const TransferDetailResponse = z
  .object({ transfer: Transfer, lines: z.array(TransferLine) })
  .openapi("TransferDetailResponse");

export const TransferReceiveResponse = z
  .object({ transfer: Transfer, lines: z.array(TransferLine), complete: z.boolean() })
  .openapi("TransferReceiveResponse");

export const TransferReceiveRequest = z
  .object({
    lines: z
      .array(
        z.object({
          line_id: Uuid,
          received_qty: InventoryPositiveMeasure,
          uom: z.string().optional(),
          variance_reason: z.string().max(500).optional(),
        }),
      )
      .min(1),
    to_location_id: Uuid.optional(),
  })
  .openapi("TransferReceiveRequest");

export const TransferCancelRequest = z
  .object({ reason: z.string().min(1).max(2000) })
  .openapi("TransferCancelRequest");

// --- cycle counts (§5 rows 37–46; §3.4) ---

export const CycleCountCreateRequest = z
  .object({
    scope_location_id: Uuid,
    blind: z.boolean().default(true),
    product_family_id: Uuid.optional(),
  })
  .openapi("CycleCountCreateRequest");

export const CycleCountListResponse = z
  .object({ items: z.array(CycleCount), next_cursor: Cursor })
  .openapi("CycleCountListResponse");

export const CycleCountDetailResponse = z
  .object({ count: CycleCount, lines: z.array(CycleCountLine) })
  .openapi("CycleCountDetailResponse");

export const CountLineCountRequest = z
  .object({
    counted_qty_base: InventoryMeasure.optional(),
    counted_measure: InventoryMeasure.optional(),
    note: z.string().max(2000).optional(),
  })
  .openapi("CountLineCountRequest");

export const CountLineAddRequest = z
  .object({
    location_id: Uuid,
    product_id: Uuid.optional(),
    stock_unit_id: Uuid.optional(),
    lot_id: Uuid.optional(),
  })
  .openapi("CountLineAddRequest");

export const CountLineReviewRequest = z
  .object({
    action: z.enum(["accept", "reject", "recount"]),
    note: z.string().max(2000).optional(),
  })
  .openapi("CountLineReviewRequest");

/** §3.4.6–7 approve outcomes: `posted: true` (tier sufficed, movements
 * posted); `posted: false` with `escalated: true` (above the caller's local
 * tier → pending_approval, D-049); `posted: false` with `moved_line_ids`
 * (§9.11 — accepted lines whose FRESH level no longer matches the snapshot
 * were flipped to recount and the count returned to in_progress; nothing
 * posted). `approval_request_id` is non-null only when Module 12's chain
 * opened one (port bound). */
export const CountApproveResponse = z
  .object({
    approval_request_id: Uuid.nullable(),
    posted: z.boolean(),
    escalated: z.boolean().optional(),
    moved_line_ids: z.array(Uuid).optional(),
  })
  .openapi("CountApproveResponse");

export const CountCancelRequest = z
  .object({ reason: z.string().min(1).max(2000) })
  .openapi("CountCancelRequest");

export const CountScheduleCreateRequest = z
  .object({
    facility_id: Uuid,
    name: z.string().min(1).max(120),
    scope_location_id: Uuid,
    product_family_id: Uuid.optional(),
    frequency: CountFrequency,
    next_run_on: BusinessDate,
    blind: z.boolean().default(true),
  })
  .openapi("CountScheduleCreateRequest");

export const CountSchedulePatchRequest = z
  .object({
    name: z.string().min(1).max(120).optional(),
    frequency: CountFrequency.optional(),
    next_run_on: BusinessDate.optional(),
    blind: z.boolean().optional(),
    is_active: z.boolean().optional(),
  })
  .openapi("CountSchedulePatchRequest");

export const CountScheduleListResponse = z
  .object({ items: z.array(CycleCountSchedule), next_cursor: Cursor })
  .openapi("CountScheduleListResponse");

// --- adjustment requests (§5 row 48; §2.12/§3.9) ---

export const AdjustmentRequestListResponse = z
  .object({ items: z.array(AdjustmentRequest), next_cursor: Cursor })
  .openapi("AdjustmentRequestListResponse");

export const AdjustmentRequestDetailResponse = z
  .object({ request: AdjustmentRequest })
  .openapi("AdjustmentRequestDetailResponse");

/** §5 row 48 (amended 2026-08-01, 2e): deliberately route a manual adjustment
 * to approval regardless of the value gate — the same wire shape as the §5
 * row-25 manual adjustment, movement_type pinned. */
export const AdjustmentRequestCreateRequest = z
  .object({
    movement_type: z.literal("adjustment"),
    product_id: Uuid,
    lot_id: Uuid.optional(),
    from_location_id: Uuid,
    to_location_id: Uuid,
    quantity: InventoryPositiveMeasure,
    uom: z.string(),
    reason_code: MovementReasonCode,
    note: z.string().min(10).max(2000),
    moved_at: isoDateTime().optional(),
    /** The work order this adjustment is recorded against (spec 06 §3.9, D-391 ruling 3); 404 WORK_ORDER_NOT_FOUND when no such order exists. */
    work_order_id: Uuid.optional(),
  })
  .openapi("AdjustmentRequestCreateRequest");

/** §3.9/D-049 local-tier decide (APPROVAL_CHAIN_PORT takes over on bind).
 * A rejection must say why (the note becomes reject_reason). */
export const AdjustmentRequestDecideRequest = z
  .object({
    action: z.enum(["approve", "reject"]),
    note: z.string().max(2000).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.action === "reject" && (data.note === undefined || data.note.trim().length === 0)) {
      ctx.addIssue({
        code: "custom",
        path: ["note"],
        message: "A rejection requires a note (spec 06 §3.9 — the chain's note)",
      });
    }
  })
  .openapi("AdjustmentRequestDecideRequest");

export const AdjustmentRequestCancelRequest = z
  .object({ note: z.string().max(2000).optional() })
  .openapi("AdjustmentRequestCancelRequest");

// --- inventory settings (§2.13, §5 row 49; D-042/D-049) ---

/** The §2.13 knobs. Money knobs serialize with the §7 redaction applied to
 * nobody — the surface itself is admin-gated (see §5 row 49). */
export const InventorySettingsResponse = z
  .object({
    cut_tolerance_pct: z.string(),
    remeasure_tolerance_pct: z.string(),
    returnable_kinds: z.array(z.string()),
    scan_context_window_seconds: z.number().int(),
    adjustment_value_threshold_minor: MinorMoney.nullable(),
    threshold_currency: Currency.nullable(),
    count_auto_approve_minor: MinorMoney.nullable(),
    facility_approve_threshold_minor: MinorMoney.nullable(),
    approval_tier_currency: Currency.nullable(),
    in_transit_stale_days: z.number().int(),
    quote_hold_expiry_hours: z.number().int(),
    // The agent reserve cap left this surface in K-1 (D-526 ruling 1): it is
    // the `inventory.reserve_stock` row's `policy_params` in module 14.
  })
  .openapi("InventorySettingsResponse");

/** PATCH semantics: only provided fields change; money knobs travel WITH
 * their currency (the §2.13 CHECK pairs, refused here as a 422 not a DB 500).
 * Clearing a money knob = explicit null for the value AND its currency. */
export const InventorySettingsPatchRequest = z
  .object({
    cut_tolerance_pct: z
      .string()
      .regex(/^\d{1,3}(\.\d{1,3})?$/)
      .optional(),
    remeasure_tolerance_pct: z
      .string()
      .regex(/^\d{1,3}(\.\d{1,3})?$/)
      .optional(),
    returnable_kinds: z.array(StockUnitKind).optional(),
    scan_context_window_seconds: z.number().int().positive().optional(),
    adjustment_value_threshold_minor: MinorMoney.min(0).nullable().optional(),
    threshold_currency: Currency.nullable().optional(),
    count_auto_approve_minor: MinorMoney.min(0).nullable().optional(),
    facility_approve_threshold_minor: MinorMoney.min(0).nullable().optional(),
    approval_tier_currency: Currency.nullable().optional(),
    in_transit_stale_days: z.number().int().positive().optional(),
    quote_hold_expiry_hours: z.number().int().positive().optional(),
  })
  .openapi("InventorySettingsPatchRequest");

/**
 * Problem codes the module-06 inventory surface raises (UPPER_SNAKE, RFC 9457;
 * D-022). The plan's canonical Phase-1 set: movement legality (§2.4),
 * concurrent cuts (§9.1), ATP (§3.6/§5 row 29), conversions (§2.10/§9.6),
 * negative stock (§9.5), the location tree (§2.1), counts + transfers state
 * machines (§3.3/§3.4), adjustment holds (§3.9) and reversals (§3.9). The
 * kernel's own arithmetic/cut/genealogy codes (MEASURE_*, CUT_*,
 * GENEALOGY_DEPTH_CAP — spec 06 §3.2 table, §2.9) live with the kernel and are
 * mapped onto transport codes by the Phase-2 services.
 *
 * DELIBERATELY NOT LISTED (amended 2026-08-02, four-slice audit — mirroring the
 * module-02 catalog's identical carve-out): the PLATFORM-level codes this
 * surface can raise but does not own — `RESOURCE_BUSY` (503, the bounded
 * advisory-lock acquire in `apps/api/src/platform/problems.ts`),
 * `VALIDATION_FAILED`, `IDEMPOTENCY_*` transport failures, and the agent-tool
 * runtime's `AGENT_TOOL_NOT_AVAILABLE` / `AGENT_TOOL_PARAMS_INVALID` /
 * `AGENT_TOOL_TENANT_PARAM_FORBIDDEN`. §5 quotes `RESOURCE_BUSY` because a
 * client must handle it; it belongs to the platform's catalog, not this one.
 *
 * The §9.9 NOT-FOUND FAMILY is listed, in full (added 2026-08-02, audit). §5
 * has always enumerated `LOCATION_NOT_FOUND` / `MOVEMENT_NOT_FOUND` /
 * `PRODUCT_NOT_FOUND` / `UNIT_NOT_FOUND` / `LOT_NOT_FOUND` /
 * `FACILITY_NOT_FOUND` as codes this surface raises — 40+ service call sites
 * raise them — while the "canonical catalog" omitted them, so the one artefact
 * a client is told to look codes up in did not carry a third of the 404s it
 * would receive.
 */
export const inventoryProblemCodes = [
  "MOVEMENT_ILLEGAL",
  "INSUFFICIENT_REMAINING",
  "INSUFFICIENT_ATP",
  "UOM_NOT_CONVERTIBLE",
  "NEGATIVE_STOCK_BLOCKED",
  "LOCATION_HIERARCHY_INVALID",
  "VIRTUAL_LOCATION_IMMUTABLE",
  "LOCATION_NOT_COUNTABLE",
  "SERIAL_TAKEN",
  "RESERVATION_CONFLICT",
  "CUT_TOLERANCE_EXCEEDED",
  "LOT_NOT_RELEASED",
  "TRANSFER_STATE_INVALID",
  "COUNT_STATE_INVALID",
  "ADJUSTMENT_REQUEST_STALE_STOCK",
  "MOVEMENT_ALREADY_REVERSED",
  "REVERSAL_NOT_REVERSIBLE",
  "UNIT_NOT_AVAILABLE",
  // Phase 2a additions (the ledger + locations slice):
  // - MEASURE_PRECISION_LOSS: the kernel's exactness guard surfaced on the
  //   wire (spec 06 §2.10/§9.7) — a conversion that would round a real
  //   quantity to nothing (or a >6-dp quantity that would silently lose a
  //   digit) is refused BEFORE any write, never rounded away.
  // - ALLOW_NEGATIVE_PRODUCTION_ONLY: §9.5 — allow_negative=true is refused on
  //   any location kind but `production` (DB CHECK backstop; this is the 422).
  // - LOCATION_CODE_TAKEN: UNIQUE(tenant, facility, code) surfaced as a 409.
  "MEASURE_PRECISION_LOSS",
  "ALLOW_NEGATIVE_PRODUCTION_ONLY",
  "LOCATION_CODE_TAKEN",
  // Review fixes 2026-08-01:
  // - ADJUSTMENT_APPROVAL_REQUIRED: the §3.9/§2.13 fail-closed interim — with
  //   no adjustment_value_threshold_minor configured (NULL for every tenant)
  //   the spec routes EVERY manual adjustment to approval; until the
  //   adjustments/approvals slice (2e) mounts that routing, POST /movements
  //   type `adjustment` is refused with 422 rather than posting unlimited-value
  //   adjustments straight into the append-only ledger. 2e replaces the 422
  //   with post-immediately (≤ threshold) or 202 {adjustment_request_id, …}.
  // - IDEMPOTENCY_KEY_REUSE: the DOMAIN half of the §9.2 guard (same code the
  //   platform HTTP idempotency layer uses) — a key found on an existing
  //   movement whose body/linkage does not match this request is a 409, never
  //   a silent "replay" of an unrelated movement.
  "ADJUSTMENT_APPROVAL_REQUIRED",
  "IDEMPOTENCY_KEY_REUSE",
  // Phase 2b additions (lots / stock units / receipts / genealogy / trace):
  // - LOT_CODE_TAKEN: UNIQUE(tenant, product, lot_code) surfaced as a 409
  //   (mirrors LOCATION_CODE_TAKEN).
  // - LOT_STATE_INVALID: a §2.2 qc_status transition the state machine forbids
  //   (quarantining a rejected lot, releasing a released one), or a lot create
  //   violating an origin requirement (§2.2: purchased ⇒ supplier_id,
  //   produced/reprocessed ⇒ work_order_id), or a receipt naming a lot of a
  //   different product. 422.
  // - UNIT_REGISTER_INVALID: POST /stock-units registers BARE containers only
  //   (§3.10.5) — a product-carrying registration must go through POST
  //   /receipts so the stock enters via the ledger; also a receipt unit whose
  //   measure UoM cannot be derived. 422.
  // - GENEALOGY_CYCLE: the §2.9 insert-time cycle refusal (incl. self-edges) —
  //   an edge whose child is an ancestor of its parent is never written into
  //   the append-only DAG. 422.
  // - GENEALOGY_DEPTH_CAP: §2.9 — a reachability check that hits the depth-50
  //   cap without CLEARING the edge refuses it (an edge we cannot prove safe
  //   is not written); its own code so an operator can escalate a legitimately
  //   deep re-link rather than reading it as bad input. 422.
  // - PRODUCT_TRACKING_MISMATCH: D-207 — a RECEIVING receipt whose shape
  //   contradicts the product's §2 `tracking` grain. `measured_unit`/`serial`
  //   arrive as units[] (the container IS the stock); `none` arrives in bulk
  //   (there are no container identities to mint); `lot` is legal EITHER way,
  //   because a lot says something about the batch and nothing about its
  //   containers — spec 07 §10's receiving scenario legitimately posts ten
  //   sealed bags against lot-tracked PVC. Its OWN code rather than a reuse of
  //   UNIT_REGISTER_INVALID: that code means "this is not how a bare container
  //   is registered" and its Russian sentence says so, which is the wrong thing
  //   to tell a storekeeper whose delivery was refused for the opposite reason.
  //   Carries product_id, sku, tracking and received_as so the screen can name
  //   the material and offer the right form. 422, raised on the receipts route
  //   only — the WO-output door is deliberately out of scope (spec 06 §3.1.2
  //   does not yet mint the output bobbin; see ReceiptMode.enforcesTracking).
  "LOT_CODE_TAKEN",
  "LOT_STATE_INVALID",
  "UNIT_REGISTER_INVALID",
  "PRODUCT_TRACKING_MISMATCH",
  "GENEALOGY_CYCLE",
  "GENEALOGY_DEPTH_CAP",
  // Phase 2c additions (cut / remeasure / scan / lookup / ATP — spec 06 §3.2
  // planner-code table): the kernel's CutPlanError codes surfaced verbatim as
  // 422s, EXCEPT CUT_EXCEEDS_REMAINING which the §9.1 concurrent-cut contract
  // surfaces as 409 INSUFFICIENT_REMAINING (already above) with the fresh
  // remaining in the problem body, and CUT_SERIAL_TAKEN (409 — a scanned
  // pre-printed label already in use tenant-wide).
  "CUT_NO_PIECES",
  "CUT_PIECE_NOT_POSITIVE",
  "CUT_SOURCE_NEGATIVE",
  "CUT_SERIAL_TAKEN",
  "CUT_TOLERANCE_INVALID",
  "CUT_FAULT_OUT_OF_RANGE",
  // Phase 2d additions (reservations + transfers — spec 06 §2.6/§2.7/§3.3,
  // §5 catalog amended in the same PR):
  // - RESERVATION_NOT_FOUND / TRANSFER_NOT_FOUND / TRANSFER_LINE_NOT_FOUND:
  //   the §9.9 not-found family for the 2d resources (cross-tenant and
  //   out-of-scope ids indistinguishable from absent). 404.
  // - TRANSFER_VARIANCE_REASON_REQUIRED: §3.3 receive-with-variance — a line
  //   left short by a receive call MUST carry receive_variance_reason (the
  //   shortfall stays in_transit and Module 12 gets the money-valued
  //   exception; a silent short receive would strand value invisibly). 422.
  //   (INSUFFICIENT_ATP, RESERVATION_CONFLICT and TRANSFER_STATE_INVALID were
  //   already registered above; 2d gives them their raising surfaces —
  //   TRANSFER_STATE_INVALID is 409: the §3.3 concurrent double-dispatch
  //   contract is "one 409".)
  "RESERVATION_NOT_FOUND",
  "TRANSFER_NOT_FOUND",
  "TRANSFER_LINE_NOT_FOUND",
  "TRANSFER_VARIANCE_REASON_REQUIRED",
  // 2d fix round (2026-08-01):
  // - RESERVATION_INVALID: a malformed reservation create the contract could
  //   not (or, for direct service callers, did not) refuse — demand_type
  //   `transfer_line` (SYSTEM-managed per the §2.6 amendment: minted only by
  //   §3.3 dispatch, resolved only by receive/cancel; a manual one would be
  //   invisible to ATP per D-048 pt 1 yet consume the unit's one-active-hold
  //   slot), or a non-positive quantity (DB CHECK > 0 — the same
  //   belt-and-braces the movement path has). 422.
  "RESERVATION_INVALID",
  // Phase 2e additions (cycle counts + the REAL §3.9 adjustment gate —
  // spec 06 §3.4/§3.9, D-046 pt 1 closure, D-049 local approval tiers):
  // - ADJUSTMENT_REQUEST_STATE_INVALID: a decide/cancel on an
  //   adjustment_request that is no longer pending_approval (§2.12 state
  //   machine — a settled holding document is settled). 409.
  //   (COUNT_STATE_INVALID, ADJUSTMENT_REQUEST_STALE_STOCK and
  //   LOCATION_NOT_COUNTABLE were already registered above; 2e gives them
  //   their raising surfaces — COUNT_STATE_INVALID is 409 for a §3.4 action
  //   the current count status refuses and 422 for an unfulfillable request
  //   in a legal state, mirroring TRANSFER_STATE_INVALID's split.)
  "ADJUSTMENT_REQUEST_STATE_INVALID",
  // - the §9.9 not-found family for the 2e resources (cross-tenant and
  //   out-of-scope ids indistinguishable from absent). 404.
  "COUNT_NOT_FOUND",
  "COUNT_LINE_NOT_FOUND",
  "SCHEDULE_NOT_FOUND",
  "ADJUSTMENT_REQUEST_NOT_FOUND",
  // Phase 4 additions (the §8 agent-tool surface — the codes the TOOL DOOR
  // raises that no HTTP route does; registered here because this array is the
  // canonical catalog and a code invented in a handler is a code nobody can
  // look up):
  // - GENEALOGY_ROOT_INVALID: §8's `trace_lot` accepts a stock_unit_id while
  //   the genealogy graph is LOT-rooted (§3.7). A unit that carries no lot has
  //   no root, and that is a loud 422 rather than a silently empty trace — the
  //   difference between "this drum has no history recorded" and "this drum has
  //   no history", which a recall investigation must never confuse.
  // - AGENT_TOOL_INVARIANT_BROKEN: 500. `create_transfer_draft` ASSERTS that
  //   what the service returned is a draft (dispatch is always human, §8). If
  //   that ever stops being true the tool fails loudly instead of quietly
  //   reporting that it dispatched stock.
  "GENEALOGY_ROOT_INVALID",
  "AGENT_TOOL_INVARIANT_BROKEN",
  // 04-B-4 (spec 06 §2.4, D-076 pt 4): the module-06 RETURN seam
  // (`IssueService.returnForWorkOrderInTxn`) validates every return against
  // the NET issued quantity for that work order + component ON ITS OWN LEDGER
  // (Σ `issue` − Σ `material_return`) and refuses the excess with this 422 —
  // module 04's endpoint-30 negative branch raises the SAME code for its
  // per-`material_issues`-row arithmetic, so the code also lives in the
  // module-04 catalog (production/schemas.ts): one fact, two raising modules,
  // one code — the IDEMPOTENCY_KEY_REUSE precedent.
  "MATERIAL_RETURN_EXCEEDS_ISSUED",
  // The §9.9 not-found family for the entities this module resolves by id
  // (added 2026-08-02, audit — see the header). 404 in every case: a
  // cross-tenant or out-of-scope id is indistinguishable from an absent one,
  // which is why these are a family rather than per-route codes. Raised via
  // `problems.notFoundEntity(<code>, …)` across locations, movements, receipts,
  // stock-units, lots, scan, cut, remeasure, transfers, reservations, counts,
  // adjustments, atp and genealogy.
  "LOCATION_NOT_FOUND",
  "MOVEMENT_NOT_FOUND",
  "PRODUCT_NOT_FOUND",
  "UNIT_NOT_FOUND",
  "LOT_NOT_FOUND",
  "FACILITY_NOT_FOUND",
  // P3-H-3 (D-391 ruling 3): a movement or an adjustment request naming a
  // `work_order_id` module 04's WO_REF_PORT cannot find (or with the port
  // unbound) answers module 04's own 404 code: one fact, two raising
  // modules, one code, as MATERIAL_RETURN_EXCEEDS_ISSUED above.
  "WORK_ORDER_NOT_FOUND",
  // P3-H-3 (D-389 ruling 5, spec 06 §2.7): the planned-order draft transfer
  // refuses a source facility that is no longer active with 409, before any
  // row is written, so module 03's conversion answers that id and moves on.
  "TRANSFER_SOURCE_INACTIVE",
] as const;

export type InventoryProblemCode = (typeof inventoryProblemCodes)[number];
